import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import { ref, computed, watch, h } from 'vue';
export default {
  name: 'ContextMenu',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    event: {
      type: Object,
      default: null
    }
  },
  emits: ['select', 'close'],
  setup(props, {
    emit
  }) {
    const menuRef = ref(null);
    const activeSubmenu = ref(null);

    // 排序菜单项
    const sortedItems = computed(() => {
      return [...props.items].sort((a, b) => {
        if (a.priority === undefined) a.priority = 100;
        if (b.priority === undefined) b.priority = 100;
        return a.priority - b.priority;
      });
    });

    // 菜单位置
    const menuStyle = computed(() => {
      const {
        x,
        y
      } = props;
      let left = x;
      let top = y;
      if (menuRef.value) {
        const rect = menuRef.value.getBoundingClientRect();
        const maxLeft = window.innerWidth - rect.width - 10;
        const maxTop = window.innerHeight - rect.height - 10;
        left = Math.min(left, maxLeft);
        top = Math.min(top, maxTop);

        // 子菜单位置
        if (activeSubmenu.value !== null) {
          const submenu = document.querySelector('.context-menu--sub');
          if (submenu) {
            const subRect = submenu.getBoundingClientRect();
            if (left + rect.width + subRect.width > window.innerWidth) {
              left = x - subRect.width;
            }
          }
        }
      }
      return {
        left: `${left}px`,
        top: `${top}px`
      };
    });

    // 获取子菜单位置
    function getSubmenuStyle(item) {
      if (!menuRef.value) return {};
      const rect = menuRef.value.getBoundingClientRect();
      const itemIndex = props.items.findIndex(i => i === item);
      const itemElement = menuRef.value.children[itemIndex];
      if (!itemElement) return {};
      const itemRect = itemElement.getBoundingClientRect();
      return {
        left: `${rect.width}px`,
        top: `${itemRect.top}px`
      };
    }

    // 处理菜单项点击
    function handleClick(item) {
      if (item.disabled) return;
      if (item.children) {
        return;
      }
      emit('select', item);
      close();
    }

    // 关闭菜单
    function close() {
      activeSubmenu.value = null;
      emit('close');
    }

    // 处理键盘导航
    function handleKeydown(e) {
      if (!props.visible) return;
      const items = sortedItems.value.filter(i => i.type !== 'divider');
      const currentIndex = items.findIndex(i => i === items[activeSubmenu.value]);
      switch (e.key) {
        case 'ArrowDown':
          {
            e.preventDefault();
            const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
            if (items[nextIndex]?.children) {
              activeSubmenu.value = props.items.indexOf(items[nextIndex]);
            } else {
              activeSubmenu.value = null;
            }
            break;
          }
        case 'ArrowUp':
          {
            e.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
            if (items[prevIndex]?.children) {
              activeSubmenu.value = props.items.indexOf(items[prevIndex]);
            } else {
              activeSubmenu.value = null;
            }
            break;
          }
        case 'Enter':
          {
            e.preventDefault();
            const currentItem = items[currentIndex];
            if (currentItem) {
              handleClick(currentItem);
            }
            break;
          }
        case 'Escape':
          e.preventDefault();
          if (activeSubmenu.value !== null) {
            activeSubmenu.value = null;
          } else {
            close();
          }
          break;
      }
    }

    // 处理点击外部
    function handleBackdropClick(e) {
      if (e.target === e.currentTarget) {
        close();
      }
    }

    // 监听显示状态
    watch(() => props.visible, visible => {
      if (visible) {
        activeSubmenu.value = null;
        document.addEventListener('keydown', handleKeydown);
        document.addEventListener('click', handleBackdropClick);
      } else {
        document.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('click', handleBackdropClick);
      }
    });

    // 默认图标
    const icons = {
      add: () => h('svg', {
        class: 'w-4 h-4',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M12 4v16m8-8H4'
      })]),
      edit: () => h('svg', {
        class: 'w-4 h-4',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
      })]),
      delete: () => h('svg', {
        class: 'w-4 h-4',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
      })]),
      copy: () => h('svg', {
        class: 'w-4 h-4',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
      })]),
      reminder: () => h('svg', {
        class: 'w-4 h-4',
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
      })])
    };
    return {
      menuRef,
      sortedItems,
      menuStyle,
      activeSubmenu,
      getSubmenuStyle,
      handleClick,
      close,
      icons
    };
  }
};